import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, ArrowLeftRight, Download, File, FileMinus, FileSearch, FileText, Landmark, Paperclip, Plus, Receipt, RefreshCw, Wallet } from 'lucide-react';
import apiService from '../../services/api';
import WeightToggle, { BagWeight, useShowWeight } from '../../components/admin/WeightToggle';
import ClientInvoiceEditor, { InvoiceDetail, invoiceTotals } from '../../components/admin/ClientInvoiceEditor';
import { formatARS } from '../../utils/formatCurrency';
import { exportInvoiceExcel } from '../../utils/invoiceExcel';
import CollectionModal, { InvoiceCollectionStatus, TERMS_LABELS } from '../../components/admin/CollectionModal';
import MoneyInput from '../../components/admin/MoneyInput';
import AssignPaymentModal from '../../components/admin/AssignPaymentModal';
import { toProductImageUrl } from '../../utils/imageUrl';
import SupplierReceiptModal from '../../components/admin/SupplierReceiptModal';
import AttachmentDropzone from '../../components/admin/AttachmentDropzone';
import { PACK_SIZE, COLOR_LABELS, receiptColorStyle } from '../../utils/warehouseStock';
import { ColorKey } from '../../utils/pricingConfig';
import { bagWeightGrams } from '../../utils/bagWeight';
import useBodyScrollLock from '../../hooks/useBodyScrollLock';
import useEscapeToClose from '../../hooks/useEscapeToClose';
import { useAuth } from '../../contexts/AuthContext';

type PartyType = 'supplier' | 'client';

interface Movement {
  id: number;
  movementType: 'documento' | 'pago';
  category: string;
  amount: number;
  movementDate: string;
  note: string;
  checkNumber: string;
  checkBank: string;
  checkIssueDate: string;
  checkDueDate: string;
  checkStatus: string;
  attachmentPath: string;
  receiptId: number | null;
  balanceAfter: number;
  assignedPayments?:number;
  collectedPayments?:number;
  unassignedBalance?:number;
  invoice?: InvoiceDetail | null;
  allocations?: Array<{invoiceId:number;amount:number}>;
}

interface SupplierReceiptDetail {
  receiptNumber: string;
  receiptDate: string;
  amount: number;
  note: string;
  attachmentPath: string;
  items: Array<{ ancho: number; largo: number; color: string; microns?: number; totalBolsas: number; unitPrice: number | null; subtotal: number | null; priceIsFigurative?: boolean; priceFigurativeNote?: string | null; itemNote: string | null; description: string; loadStock?: boolean }>;
}

interface SupplierReceiptSummary {
  id: number;
  receiptNumber: string;
  receiptDate: string;
  amount: number;
  note: string;
  itemsCount: number;
  hasMovement: boolean;
  hasNonStockItem: boolean;
  hasFigurativePriceItem: boolean;
  amountMismatch: boolean;
  items?: Array<{ ancho: number; largo: number; color: string; microns?: number; unidad: 'bolsas' | 'packs'; cantidad: number; bolsasPorPack?: number | null; totalBolsas: number; loadStock?: boolean }>;
}

interface SupplierReceiptFull {
  id: number;
  receiptNumber: string;
  receiptDate: string;
  amount: number;
  note: string;
  attachmentPath: string;
  items: Array<{ ancho: number; largo: number; color: string; microns: number; unidad: 'bolsas' | 'packs'; cantidad: number; bolsasPorPack: number | null; unitPrice: number | null; itemNote: string | null; loadStock?: boolean }>;
}

const CATEGORY_LABELS: Record<string, string> = { remito: 'Remito', factura: 'Factura', cheque: 'Cheque', efectivo: 'Efectivo', transferencia: 'Transferencia', nota_credito: 'Nota de crédito', ajuste: 'Ajuste por redondeo', otro: 'Otro' };
// Categorías que casi siempre son de un tipo: un remito/factura genera deuda (documento);
// un cheque, efectivo, transferencia o nota de crédito la cancela (pago). "Otro" queda libre.
const CATEGORY_DEFAULT_TYPE: Record<string, 'documento' | 'pago'> = {
  remito: 'documento', factura: 'documento',
  cheque: 'pago', efectivo: 'pago', transferencia: 'pago', nota_credito: 'pago', ajuste: 'pago',
};
const CATEGORY_ICONS: Record<string, React.ComponentType<{ className?: string }>> = { remito: FileText, factura: Receipt, cheque: Landmark, efectivo: Wallet, transferencia: ArrowLeftRight, nota_credito: FileMinus, ajuste: FileMinus, otro: File };
const CHECK_STATUS_LABELS: Record<string, string> = { pendiente: 'Pendiente de depositar', depositado: 'Depositado, sin acreditar', cobrado: 'Acreditado / debitado', rechazado: 'Rechazado' };
const CHECK_STATUS_BADGE: Record<string, string> = {
  pendiente: 'bg-gray-100 text-gray-700 border-gray-200',
  depositado: 'bg-blue-100 text-blue-700 border-blue-200',
  cobrado: 'bg-green-100 text-green-700 border-green-200',
  rechazado: 'bg-red-100 text-red-700 border-red-200'
};

const today = () => new Date().toISOString().slice(0, 10);
// Pesos showing the cents only when there are any, so a stray $0,38 does not read as $0.
const formatPesos = (value: number) => `$ ${value.toLocaleString('es-AR', { minimumFractionDigits: Math.round(value * 100) % 100 ? 2 : 0, maximumFractionDigits: 2 })}`;
const formatDMY = (iso: string): string => {
  const [y, m, d] = String(iso || '').split('-');
  return d && m && y ? `${d}/${m}/${y}` : iso;
};

const emptyForm = {
  movementType: 'documento' as 'documento' | 'pago',
  category: 'remito',
  amount: 0,
  movementDate: today(),
  note: '',
  checkNumber: '',
  checkBank: '',
  checkIssueDate: today(),
  checkDueDate: today(),
  checkStatus: 'pendiente',
  attachmentPath: '',
  invoice: null as InvoiceDetail | null,
  allocations: [] as Array<{invoiceId:number;amount:number}>,
  receiptId: null as number | null
};

const DeleteConfirm: React.FC<{ onConfirm: () => void; onCancel: () => void }> = ({ onConfirm, onCancel }) => (
  <div className="inline-flex items-center gap-2 text-xs">
    <span className="text-gray-600">¿Confirmar?</span>
    <button onClick={onConfirm} className="px-2 py-1 rounded bg-red-600 text-white hover:bg-red-700">Sí</button>
    <button onClick={onCancel} className="px-2 py-1 rounded border border-gray-300 hover:bg-gray-50">No</button>
  </div>
);

interface CuentaCorrienteDetalleProps {
  partyTypeOverride?: PartyType;
  partyIdOverride?: number;
  embedded?: boolean;
}

const CuentaCorrienteDetalle: React.FC<CuentaCorrienteDetalleProps> = ({ partyTypeOverride, partyIdOverride, embedded }) => {
  const { partyType, partyId } = useParams<{ partyType: PartyType; partyId: string }>();
  const [searchParams] = useSearchParams();
  const targetMovementId = Number(searchParams.get('movementId') || 0);
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const isReadOnly = !!currentUser?.isReadOnly;
  const showWeight = useShowWeight();
  const type = (partyTypeOverride ?? (partyType === 'client' ? 'client' : 'supplier')) as PartyType;
  const id = partyIdOverride ?? Number(partyId);

  const [partyName, setPartyName] = useState('');
  const [partyCuit, setPartyCuit] = useState('');
  const [movements, setMovements] = useState<Movement[]>([]);
  const [balance, setBalance] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [formError, setFormError] = useState('');
  // Invoices preselected in the collection form; null = closed.
  const [collectionFor, setCollectionFor] = useState<number[] | null>(null);
  const [collectionTerms, setCollectionTerms] = useState('');
  const [fixingId, setFixingId] = useState<number | null>(null);
  const [assigningPayment, setAssigningPayment] = useState<Movement | null>(null);

  const [showReceiptForm, setShowReceiptForm] = useState(false);
  const [editingReceipt, setEditingReceipt] = useState<SupplierReceiptFull | null>(null);
  const [receiptDetail, setReceiptDetail] = useState<SupplierReceiptDetail | null>(null);
  const [receiptDetailError, setReceiptDetailError] = useState('');
  const [receipts, setReceipts] = useState<SupplierReceiptSummary[]>([]);
  const [confirmDeleteReceiptId, setConfirmDeleteReceiptId] = useState<number | null>(null);
  const [zoomImage, setZoomImage] = useState<string | null>(null);

  useBodyScrollLock(!!(showForm || showReceiptForm || editingReceipt || receiptDetail || receiptDetailError || zoomImage));
  useEscapeToClose(showForm, () => setShowForm(false));
  useEscapeToClose(!!(showReceiptForm || editingReceipt), () => { setShowReceiptForm(false); setEditingReceipt(null); });
  useEscapeToClose(!!(receiptDetail || receiptDetailError), () => { setReceiptDetail(null); setReceiptDetailError(''); });
  useEscapeToClose(!!zoomImage, () => setZoomImage(null));

  const loadReceipts = async () => {
    if (type !== 'supplier') return;
    try {
      const { receipts: list } = await apiService.getSupplierReceipts(id);
      setReceipts(list || []);
    } catch {
      setReceipts([]);
    }
  };

  const load = async () => {
    try {
      // Only the first load shows "Cargando...": refreshing after a save keeps the list in place,
      // so the page does not collapse and jump back to the top.
      if (!movements.length) setLoading(true);
      setError('');
      const [summary, detail] = await Promise.all([
        apiService.getAccountsSummary(type),
        apiService.getAccountMovements(type, id)
      ]);
      const party = (summary.items || []).find((item: any) => item.partyId === id);
      setPartyName(party ? (party.company ? `${party.name} (${party.company})` : party.name) : '');
      setPartyCuit(String(party?.cuit || ''));
      setMovements(detail.movements || []);
      setBalance(detail.balance || 0);
      await loadReceipts();
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'No se pudo cargar la cuenta corriente.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Another client or supplier: start clean, so the previous one's movements are never shown.
    setMovements([]);
    setLoading(true);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, id]);

  const openCreate = () => { setEditingId(null); setForm(emptyForm); setFormError(''); setShowForm(true); };
  const openCreateForReceipt = (receipt: SupplierReceiptSummary) => {
    setEditingId(null);
    setForm({
      ...emptyForm,
      category: 'remito',
      movementDate: receipt.receiptDate,
      note: receipt.receiptNumber ? `Remito ${receipt.receiptNumber}` : '',
      receiptId: receipt.id,
    });
    setFormError('');
    setShowForm(true);
  };
  const openEdit = (m: Movement) => {
    setEditingId(m.id);
    setForm({
      movementType: m.movementType,
      category: m.category,
      amount: m.amount,
      movementDate: m.movementDate,
      note: m.note,
      checkNumber: m.checkNumber,
      checkBank: m.checkBank,
      checkIssueDate: m.checkIssueDate || today(),
      checkDueDate: m.checkDueDate || today(),
      checkStatus: m.checkStatus || 'pendiente',
      attachmentPath: m.attachmentPath,
      invoice: m.invoice||null,
      allocations: m.allocations||[],
      receiptId: m.receiptId
    });
    setFormError('');
    setShowForm(true);
  };

  const handleAttachmentChange = async (file: File | undefined) => {
    if (!file) return;
    try {
      setUploading(true);
      setFormError('');
      const response = await apiService.uploadAccountAttachment(file);
      setForm(current => ({ ...current, attachmentPath: response.path }));
    } catch (e: any) {
      setFormError(e?.response?.data?.detail || 'No se pudo subir el adjunto.');
    } finally {
      setUploading(false);
    }
  };

  const submitForm = async () => {
    const detailed = type==='client' && form.category==='factura' && form.movementType==='documento' && form.invoice;
    if (detailed && (!detailed.items.length || detailed.items.some(line=>!line.productName.trim() || line.quantity<=0))) { setFormError('Completa los articulos y sus cantidades.'); return; }
    const payload = {...form, invoice:detailed||null, allocations:form.movementType==='pago'?form.allocations:[], amount:detailed&&!editingId?invoiceTotals(detailed).total:form.amount};
    if (detailed && Math.abs(invoiceTotals(detailed).total-payload.amount)>0.02) { setFormError(`El detalle suma ${formatARS(invoiceTotals(detailed).total)} y el monto registrado es ${formatARS(payload.amount)}. Revisa los precios o modifica el monto expresamente.`); return; }
    if (payload.amount <= 0) { setFormError('El monto debe ser mayor a cero.'); return; }
    const tiny = payload.allocations.find((a: {invoiceId:number;amount:number}) => a.amount < 100 && payload.amount > 1000);
    if (tiny && !window.confirm(`Asignaste ${formatARS(tiny.amount)} a una factura en un pago de ${formatARS(payload.amount)}. ¿Es correcto?`)) return;
    try {
      setSaving(true);
      setFormError('');
      if (editingId) {
        await apiService.updateAccountMovement(editingId, payload);
      } else {
        await apiService.createAccountMovement(type, id, payload);
      }
      setShowForm(false);
      await load();
    } catch (e: any) {
      setFormError(e?.response?.data?.detail || 'No se pudo guardar el movimiento.');
    } finally {
      setSaving(false);
    }
  };

  const changeCheckStatus = async (movementId: number, status: string) => {
    try {
      setError('');
      await apiService.updateAccountMovementCheckStatus(movementId, status);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'No se pudo actualizar el estado del cheque.');
    }
  };

  const deleteMovement = async (movementId: number) => {
    try {
      setError('');
      await apiService.deleteAccountMovement(movementId);
      setConfirmDeleteId(null);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'No se pudo eliminar el movimiento.');
    }
  };

  const deleteReceipt = async (receiptId: number) => {
    try {
      setError('');
      await apiService.deleteSupplierReceipt(receiptId);
      setConfirmDeleteReceiptId(null);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'No se pudo eliminar el remito.');
    }
  };

  const openReceiptDetailById = async (receiptId: number) => {
    try {
      setReceiptDetailError('');
      const { receipt } = await apiService.getSupplierReceipt(receiptId);
      setReceiptDetail(receipt);
    } catch (e: any) {
      setReceiptDetailError(e?.response?.data?.detail || 'No se pudo cargar el detalle del remito.');
    }
  };

  const openEditReceipt = async (receiptId: number) => {
    try {
      setError('');
      const { receipt } = await apiService.getSupplierReceipt(receiptId);
      setEditingReceipt(receipt);
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'No se pudo cargar el remito para editar.');
    }
  };
  const openReceiptDetail = (movement: Movement) => { if (movement.receiptId) openReceiptDetailById(movement.receiptId); };

  const openAttachment = (path: string) => {
    const url = toProductImageUrl(path);
    if (/\.pdf$/i.test(path)) {
      window.open(url, '_self');
    } else {
      setZoomImage(url);
    }
  };

  const balanceLabel = type === 'supplier' ? 'Le debo' : 'Me debe';

  // Cada remito de proveedor con monto ya tiene su propio movimiento "documento"
  // en account_movements (categoria 'remito'). En vez de mostrar el remito dos
  // veces (una como movimiento, otra como remito cargado), se combinan en una
  // sola fila por remito, y el resto de los movimientos (pagos, cheques, etc.)
  // se listan igual que antes.
  type CombinedRow =
    | { kind: 'movement'; key: string; date: string; movement: Movement }
    | { kind: 'receipt'; key: string; date: string; receipt: SupplierReceiptSummary; movement: Movement | null };

  const combinedRows = useMemo<CombinedRow[]>(() => {
    if (type !== 'supplier') {
      return movements.map(m => ({ kind: 'movement' as const, key: `m-${m.id}`, date: m.movementDate, movement: m }));
    }
    const remitoMovementByReceiptId = new Map<number, Movement>();
    movements.forEach(m => { if (m.category === 'remito' && m.receiptId != null) remitoMovementByReceiptId.set(m.receiptId, m); });
    const rows: CombinedRow[] = [
      ...movements.filter(m => m.category !== 'remito').map(m => ({ kind: 'movement' as const, key: `m-${m.id}`, date: m.movementDate, movement: m })),
      ...receipts.map(r => ({ kind: 'receipt' as const, key: `r-${r.id}`, date: r.receiptDate, receipt: r, movement: remitoMovementByReceiptId.get(r.id) || null })),
    ];
    const isDocTier = (row: CombinedRow) => row.kind === 'receipt' || row.movement.movementType === 'documento';
    const rowId = (row: CombinedRow) => row.kind === 'movement' ? row.movement.id : -row.receipt.id;
    return rows.sort((a, b) => {
      if (a.date !== b.date) return a.date > b.date ? -1 : 1;
      const aTier = isDocTier(a) ? 1 : 0;
      const bTier = isDocTier(b) ? 1 : 0;
      if (aTier !== bTier) return aTier - bTier;
      return rowId(b) - rowId(a);
    });
  }, [movements, receipts, type]);
  useEffect(()=>{
    if(loading||!targetMovementId) return;
    window.setTimeout(()=>{
      const rows=Array.from(document.querySelectorAll<HTMLElement>(`[data-movement-id="${targetMovementId}"]`));
      (rows.find(row=>row.offsetParent!==null)||rows[0])?.scrollIntoView({behavior:'smooth',block:'center'});
    },0);
  },[loading,targetMovementId,movements]);

  // A client invoice already fully covered by payments (cheques assigned or collected) is shaded green.
  const isCoveredInvoice = (m: Movement) => type === 'client' && m.movementType === 'documento' && m.category === 'factura' && Number(m.assignedPayments || 0) > 0 && Number(m.unassignedBalance ?? m.amount) <= 0.01;
  const rejectedChequeCount = movements.filter(m => m.category === 'cheque' && m.checkStatus === 'rechazado').length;
  const pendingInvoices = movements.filter(m => m.movementType === 'documento' && m.category === 'factura' && Number(m.unassignedBalance ?? m.amount) > 0.01)
    .map(m => ({ id: m.id, number: m.invoice?.number || '', date: m.movementDate, amount: m.amount, remaining: Number(m.unassignedBalance ?? m.amount) }));
  const invoiceLabel = (invoiceId: number) => movements.find(f => f.id === invoiceId)?.invoice?.number || `del ${formatDMY(movements.find(f => f.id === invoiceId)?.movementDate || '')}`;
  // Payments whose assignment is a tiny part of their amount: almost always a typo (e.g. "1.157.957" read as 1,16).
  const suspiciousPayments = type === 'client' ? movements.filter(m => m.movementType === 'pago' && m.amount > 1000 && !!m.allocations?.length
    && m.allocations.reduce((sum, a) => sum + a.amount, 0) < m.amount * 0.01) : [];
  const fixSuspicious = async (m: Movement) => {
    const allocation = m.allocations![0];
    const invoice = movements.find(f => f.id === allocation.invoiceId);
    if (!invoice) return;
    const available = Number(invoice.unassignedBalance ?? 0) + allocation.amount;
    try {
      setFixingId(m.id); setError('');
      await apiService.updateAccountMovement(m.id, { allocations: [{ invoiceId: allocation.invoiceId, amount: Math.round(Math.min(m.amount, available) * 100) / 100 }, ...m.allocations!.slice(1)] });
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'No se pudo corregir la asignación.');
    } finally { setFixingId(null); }
  };
  useEffect(() => {
    if (type !== 'client') return;
    let active = true;
    apiService.getCollectionTerms(id).then(({ terms }) => { if (active) setCollectionTerms(terms); }).catch(() => {});
    return () => { active = false; };
  }, [type, id]);
  const closeDifference = async (m: Movement) => {
    const remaining = Number(m.unassignedBalance ?? 0);
    if (!window.confirm(`Cerrar la diferencia de ${formatPesos(remaining)} de la factura ${m.invoice?.number || `del ${formatDMY(m.movementDate)}`} con un ajuste por redondeo? Baja lo que debe el cliente, pero no cuenta como plata cobrada.`)) return;
    try { setError(''); await apiService.closeInvoiceDifference(id, m.id); await load(); }
    catch (e: any) { setError(e?.response?.data?.detail || 'No se pudo cerrar la diferencia.'); }
  };
  const changeTerms = async (terms: string) => {
    try { setCollectionTerms((await apiService.setCollectionTerms(id, terms)).terms); }
    catch (e: any) { setError(e?.response?.data?.detail || 'No se pudo guardar la condición de cobro.'); }
  };

  return (
    <div className="space-y-6">
      {!embedded && <button onClick={() => navigate('/admin/cuentas-corrientes')} className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900"><ArrowLeft className="h-4 w-4" />Volver a cuentas corrientes</button>}

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          {embedded ? <p className="text-sm text-gray-500">Cuenta corriente</p> : <h1 className="text-2xl font-bold text-gray-900">{partyName || (type === 'supplier' ? 'Proveedor' : 'Cliente')}</h1>}
          <p className={`${embedded ? 'text-2xl' : 'text-lg'} font-semibold ${balance > 0 ? 'text-red-600' : balance < 0 ? 'text-green-700' : 'text-gray-500'}`}>{balanceLabel}: {formatARS(balance)}</p>
        </div>
        <div className="flex flex-wrap gap-2 [&>button]:flex-1 sm:[&>button]:flex-none [&>button]:justify-center">
          {!isReadOnly && type === 'supplier' && <button onClick={() => setShowReceiptForm(true)} className="inline-flex items-center gap-2 rounded-lg border border-primary-600 px-3 py-2 text-sm text-primary-600 hover:bg-primary-50"><Plus className="h-4 w-4" />Remito de proveedor</button>}
          {!isReadOnly && type === 'client' && <button onClick={() => setCollectionFor([])} disabled={!pendingInvoices.length} title={pendingInvoices.length ? '' : 'No hay facturas con saldo pendiente'} className="inline-flex items-center gap-2 rounded-lg border border-emerald-600 px-3 py-2 text-sm text-emerald-700 hover:bg-emerald-50 disabled:opacity-40"><Landmark className="h-4 w-4" />Cargar cobro</button>}
          {!isReadOnly && <button onClick={openCreate} className="inline-flex items-center gap-2 rounded-lg bg-primary-600 px-3 py-2 text-sm text-white"><Plus className="h-4 w-4" />Nuevo movimiento</button>}
          <button onClick={load} className="inline-flex items-center gap-2 rounded-lg border bg-white px-3 py-2 text-sm"><RefreshCw className="h-4 w-4" />Actualizar</button>
        </div>
      </div>

      {error && <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-red-700">{error}</div>}

      {rejectedChequeCount > 0 && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {rejectedChequeCount} cheque{rejectedChequeCount === 1 ? '' : 's'} rechazado{rejectedChequeCount === 1 ? '' : 's'} sin regularizar — {balanceLabel.toLowerCase()} ya vuelve a contar esa deuda, pero conviene resolverlo con {type === 'supplier' ? 'el proveedor' : 'el cliente'} (nuevo cheque, efectivo, etc.).
        </div>
      )}

      {type === 'client' && (
        <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800">
          <p>Los remitos registran entregas. Cargá la factura con "Nuevo movimiento", categoría Factura. Cuando tesorería pague, usá <strong>"Cargar cobro"</strong>: los cheques se reparten solos entre las facturas.</p>
          {collectionTerms && <label className="mt-2 inline-flex flex-wrap items-center gap-2 text-xs font-medium">Condición de cobro habitual:
            <select value={collectionTerms} disabled={isReadOnly} onChange={e => changeTerms(e.target.value)} className="rounded border border-blue-200 bg-white px-2 py-1 text-xs">{Object.entries(TERMS_LABELS).map(([key, label]) => <option key={key} value={key}>{label}</option>)}</select>
          </label>}
        </div>
      )}

      {suspiciousPayments.length > 0 && (
        <div className="space-y-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
          <p className="font-semibold">{suspiciousPayments.length} pago{suspiciousPayments.length === 1 ? '' : 's'} con una asignación a factura que parece mal cargada</p>
          {suspiciousPayments.map(m => (
            <div key={m.id} className="flex flex-wrap items-center justify-between gap-2 rounded border border-amber-200 bg-white px-3 py-2 text-xs">
              <span>{m.category === 'cheque' ? `Cheque ${m.checkNumber || 's/n'}` : CATEGORY_LABELS[m.category] || m.category} de {formatARS(m.amount)} · {formatDMY(m.movementDate)} — asignado a la factura {invoiceLabel(m.allocations![0].invoiceId)} solo {formatARS(m.allocations!.reduce((sum, a) => sum + a.amount, 0))}</span>
              {!isReadOnly && <button type="button" disabled={fixingId === m.id} onClick={() => fixSuspicious(m)} className="rounded border border-amber-400 px-2 py-1 font-medium hover:bg-amber-100 disabled:opacity-50">{fixingId === m.id ? 'Corrigiendo...' : 'Asignar el total'}</button>}
            </div>
          ))}
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-gray-100">
      <div className="divide-y md:hidden">
        {loading ? <div className="px-5 py-6 text-center text-gray-500">Cargando...</div> :
          combinedRows.map(row => {
            if (row.kind === 'movement') {
              const m = row.movement;
              const CategoryIcon = CATEGORY_ICONS[m.category] || File;
              const hasDetail = m.category==='factura' || m.invoice || m.allocations?.length || (type==='client' && m.movementType==='pago') || m.category === 'cheque' || m.receiptId || m.attachmentPath;
              return (
                <div key={row.key} data-movement-id={m.id} className={`p-4 space-y-2 ${m.id===targetMovementId?'bg-amber-50 ring-2 ring-inset ring-amber-300':isCoveredInvoice(m)?'bg-green-50':''}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">{formatDMY(m.movementDate)}</span>
                    {m.movementType === 'documento' ? <span className="text-red-600 font-medium text-sm">Documento</span> : <span className="text-green-700 font-medium text-sm">Pago</span>}
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    {m.attachmentPath ? (
                      <button type="button" onClick={() => openAttachment(m.attachmentPath)} className="inline-flex items-center gap-1.5 text-sm text-gray-700 hover:text-primary-600">
                        <CategoryIcon className="h-3.5 w-3.5 text-gray-400" />{CATEGORY_LABELS[m.category] || m.category}
                      </button>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 text-sm"><CategoryIcon className="h-3.5 w-3.5 text-gray-400" />{CATEGORY_LABELS[m.category] || m.category}</span>
                    )}
                    <span className="font-semibold">{formatARS(m.amount)}</span>
                  </div>
                  {hasDetail && (
                    <div className="rounded-lg bg-gray-50 p-2 space-y-1.5">
                      {m.category==='factura'&&m.movementType==='documento'&&(type==='client'?<InvoiceCollectionStatus invoice={m} movements={movements} onCollect={isReadOnly?undefined:()=>setCollectionFor([m.id])} onCloseDifference={isReadOnly?undefined:()=>closeDifference(m)}/>:<p className="text-xs text-gray-600">Pagos asignados: {formatARS(m.assignedPayments||0)} · Cobrado: {formatARS(m.collectedPayments||0)} · Sin pago asignado: {formatARS(m.unassignedBalance??m.amount)}</p>)}
                        {m.invoice&&<div className="text-xs text-blue-800"><p className="font-semibold">Factura {m.invoice.number||'sin número'}</p>{m.invoice.items.map((line,index)=><p key={index}>{line.productName}{line.microns?` · ${line.microns}μ`:''}: {line.quantity/PACK_SIZE} packs{line.orderMonth?` · ${line.orderLabel||line.orderMonth}`:''}</p>)}<p>Sin IVA {formatARS(m.invoice.net||0)} · IVA {formatARS(m.invoice.vat||0)}</p><button type="button" onClick={()=>exportInvoiceExcel(m.invoice!,{clientName:partyName,clientCuit:partyCuit,date:m.movementDate,note:m.note})} className="mt-1 inline-flex items-center gap-1 rounded border border-emerald-300 bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-800 hover:bg-emerald-100"><Download className="h-3 w-3"/>Excel</button></div>}
                        {type==='client'&&m.movementType==='pago'&&(()=>{const allocated=(m.allocations||[]).reduce((s,a)=>s+a.amount,0);const free=m.amount-allocated;return <div className="space-y-0.5 text-xs text-blue-800">{(m.allocations||[]).map(a=><p key={a.invoiceId}>Paga factura {invoiceLabel(a.invoiceId)}: {formatARS(a.amount)}</p>)}{!allocated?<p className="font-semibold text-orange-700">No está asignado a ninguna factura</p>:free>=1&&<p className="text-amber-700">Sin asignar: {formatARS(free)}</p>}{!isReadOnly&&m.category!=='nota_credito'&&<button type="button" onClick={()=>setAssigningPayment(m)} className="rounded border border-primary-300 bg-white px-2 py-0.5 font-medium text-primary-700 hover:bg-primary-50">{allocated?'Cambiar factura':'Asignar a factura'}</button>}</div>;})()}
                        {m.category === 'cheque' && (
                        <>
                          <p className="text-xs text-gray-500">{m.checkNumber || <span className="rounded bg-amber-100 px-1.5 py-0.5 font-semibold text-amber-800">Falta el número</span>}{m.checkBank ? ` · ${m.checkBank}` : ''}</p>
                          <p className="text-xs text-gray-400">Emisión: {m.checkIssueDate ? formatDMY(m.checkIssueDate) : '-'} · Vence: {m.checkDueDate ? formatDMY(m.checkDueDate) : '-'}</p>
                          {isReadOnly ? (
                            <span className={`inline-flex rounded-full border px-2 py-1 text-xs font-semibold ${CHECK_STATUS_BADGE[m.checkStatus] || CHECK_STATUS_BADGE.pendiente}`}>{CHECK_STATUS_LABELS[m.checkStatus] || m.checkStatus}</span>
                          ) : (
                            <select value={m.checkStatus} onChange={e => changeCheckStatus(m.id, e.target.value)} className={`rounded-full border px-2 py-1 text-xs font-semibold ${CHECK_STATUS_BADGE[m.checkStatus] || CHECK_STATUS_BADGE.pendiente}`}>
                              {Object.entries(CHECK_STATUS_LABELS).map(([key, label]) => <option key={key} value={key} disabled={key === 'cobrado' && m.checkDueDate > today()}>{label}</option>)}
                            </select>
                          )}
                        </>
                      )}
                      {m.receiptId && <button onClick={() => openReceiptDetail(m)} className="flex items-center gap-1 text-xs text-primary-600"><FileSearch className="h-3.5 w-3.5" />Ver remito</button>}
                      {m.attachmentPath && <button type="button" onClick={() => openAttachment(m.attachmentPath)} className="flex items-center gap-1 text-xs text-primary-600"><Paperclip className="h-3.5 w-3.5" />Ver comprobante</button>}
                    </div>
                  )}
                  {m.note && <p className="text-sm text-gray-500">{m.note}</p>}
                  {!isReadOnly && (
                    <div className="flex justify-end gap-2 pt-1">
                      <button onClick={() => openEdit(m)} className="rounded-lg border px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">Editar</button>
                      {confirmDeleteId === m.id ? (
                        <DeleteConfirm onConfirm={() => deleteMovement(m.id)} onCancel={() => setConfirmDeleteId(null)} />
                      ) : (
                        <button onClick={() => setConfirmDeleteId(m.id)} className="rounded-lg border border-red-200 px-3 py-2 text-sm text-red-600 hover:bg-red-50">Eliminar</button>
                      )}
                    </div>
                  )}
                </div>
              );
            }
            const r = row.receipt;
            const rm = row.movement;
            return (
              <div key={row.key} className="p-4 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">{formatDMY(r.receiptDate)}</span>
                  <span className="text-red-600 font-medium text-sm">Documento</span>
                </div>
                <div className="flex items-center justify-between gap-2">
                  {rm?.attachmentPath ? (
                    <button type="button" onClick={() => openAttachment(rm.attachmentPath)} className="inline-flex items-center gap-1.5 text-sm text-gray-700 hover:text-primary-600">
                      <FileText className="h-3.5 w-3.5 text-gray-400" />Remito {r.receiptNumber || ''}
                    </button>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 text-sm"><FileText className="h-3.5 w-3.5 text-gray-400" />Remito {r.receiptNumber || ''}</span>
                  )}
                  {rm ? <span className="font-semibold">{formatARS(rm.amount)}</span> : <span className="rounded-full bg-amber-100 px-2 py-1 text-xs font-semibold text-amber-800">Sin monto</span>}
                </div>
                <div className="rounded-lg bg-gray-50 p-2 space-y-1.5">
                  <p className="text-xs text-gray-600">
                    {r.itemsCount} línea{r.itemsCount === 1 ? '' : 's'}
                    {r.hasNonStockItem && <span title="Alguna línea de este remito no carga a Stock propio" className="ml-1 inline-block rounded-full bg-gray-100 px-2 py-1 text-xs font-semibold text-gray-600">No carga stock</span>}
                    {r.hasFigurativePriceItem && <span title="Alguna línea tiene precio solo de referencia, no incluido en el monto total" className="ml-1 inline-block rounded-full bg-purple-100 px-2 py-1 text-xs font-semibold text-purple-700">Precio de ref.</span>}
                    {r.amountMismatch && <span title="El monto cargado no coincide con la suma de las líneas" className="ml-1 inline-block rounded-full bg-amber-100 px-2 py-1 text-xs font-semibold text-amber-800">⚠ no coincide</span>}
                  </p>
                  <button onClick={() => openReceiptDetailById(r.id)} className="flex items-center gap-1 text-xs text-primary-600"><FileSearch className="h-3.5 w-3.5" />Ver detalle</button>
                </div>
                {r.note && <p className="text-sm text-gray-500">{r.note}</p>}
                {!isReadOnly && (
                  <div className="flex flex-wrap justify-end gap-2 pt-1">
                    <button onClick={() => openEditReceipt(r.id)} className="rounded-lg border px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">Editar</button>
                    {!r.hasMovement && <button onClick={() => openCreateForReceipt(r)} className="rounded-lg bg-primary-600 px-3 py-2 text-sm text-white hover:bg-primary-700">Cargar monto</button>}
                    {confirmDeleteReceiptId === r.id ? (
                      <DeleteConfirm onConfirm={() => deleteReceipt(r.id)} onCancel={() => setConfirmDeleteReceiptId(null)} />
                    ) : (
                      <button onClick={() => setConfirmDeleteReceiptId(r.id)} className="rounded-lg border border-red-200 px-3 py-2 text-sm text-red-600 hover:bg-red-50">Eliminar</button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        {!loading && !combinedRows.length && <div className="px-5 py-10 text-center text-gray-500">Todavía no hay movimientos cargados.</div>}
      </div>
      <div className="hidden md:block overflow-x-auto">
        <table className="min-w-full">
          <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
            <tr><th className="px-5 py-3">Fecha</th><th className="px-5 py-3">Tipo</th><th className="px-5 py-3">Categoría</th><th className="px-5 py-3">Monto</th><th className="px-5 py-3">Detalle</th><th className="px-5 py-3">Nota</th><th className="px-5 py-3"></th></tr>
          </thead>
          <tbody className="divide-y">
            {loading ? <tr><td colSpan={7} className="px-5 py-6 text-center text-gray-500">Cargando...</td></tr> :
              combinedRows.map(row => {
                if (row.kind === 'movement') {
                  const m = row.movement;
                  const CategoryIcon = CATEGORY_ICONS[m.category] || File;
                  return (
                  <tr key={row.key} data-movement-id={m.id} className={m.id===targetMovementId?'bg-amber-50 ring-2 ring-inset ring-amber-300':isCoveredInvoice(m)?'bg-green-50':undefined}>
                    <td className="px-5 py-3 text-sm text-gray-600">{formatDMY(m.movementDate)}</td>
                    <td className="px-5 py-3">{m.movementType === 'documento' ? <span className="text-red-600 font-medium">Documento</span> : <span className="text-green-700 font-medium">Pago</span>}</td>
                    <td className="px-5 py-3">
                      {m.attachmentPath ? (
                        <button type="button" onClick={() => openAttachment(m.attachmentPath)} className="inline-flex items-center gap-1.5 text-gray-700 hover:text-primary-600" title="Ver comprobante">
                          <CategoryIcon className="h-3.5 w-3.5 text-gray-400" />{CATEGORY_LABELS[m.category] || m.category}
                        </button>
                      ) : (
                        <span className="inline-flex items-center gap-1.5"><CategoryIcon className="h-3.5 w-3.5 text-gray-400" />{CATEGORY_LABELS[m.category] || m.category}</span>
                      )}
                    </td>
                    <td className="px-5 py-3 font-semibold">{formatARS(m.amount)}</td>
                    <td className="px-5 py-3">
                      <div className="space-y-1">
                        {m.category==='factura'&&m.movementType==='documento'&&(type==='client'?<InvoiceCollectionStatus invoice={m} movements={movements} onCollect={isReadOnly?undefined:()=>setCollectionFor([m.id])} onCloseDifference={isReadOnly?undefined:()=>closeDifference(m)}/>:<p className="text-xs text-gray-600">Pagos asignados: {formatARS(m.assignedPayments||0)} · Cobrado: {formatARS(m.collectedPayments||0)} · Sin pago asignado: {formatARS(m.unassignedBalance??m.amount)}</p>)}
                        {m.invoice&&<div className="text-xs text-blue-800"><p className="font-semibold">Factura {m.invoice.number||'sin número'}</p>{m.invoice.items.map((line,index)=><p key={index}>{line.productName}{line.microns?` · ${line.microns}μ`:''}: {line.quantity/PACK_SIZE} packs{line.orderMonth?` · ${line.orderLabel||line.orderMonth}`:''}</p>)}<p>Sin IVA {formatARS(m.invoice.net||0)} · IVA {formatARS(m.invoice.vat||0)}</p><button type="button" onClick={()=>exportInvoiceExcel(m.invoice!,{clientName:partyName,clientCuit:partyCuit,date:m.movementDate,note:m.note})} className="mt-1 inline-flex items-center gap-1 rounded border border-emerald-300 bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-800 hover:bg-emerald-100"><Download className="h-3 w-3"/>Excel</button></div>}
                        {type==='client'&&m.movementType==='pago'&&(()=>{const allocated=(m.allocations||[]).reduce((s,a)=>s+a.amount,0);const free=m.amount-allocated;return <div className="space-y-0.5 text-xs text-blue-800">{(m.allocations||[]).map(a=><p key={a.invoiceId}>Paga factura {invoiceLabel(a.invoiceId)}: {formatARS(a.amount)}</p>)}{!allocated?<p className="font-semibold text-orange-700">No está asignado a ninguna factura</p>:free>=1&&<p className="text-amber-700">Sin asignar: {formatARS(free)}</p>}{!isReadOnly&&m.category!=='nota_credito'&&<button type="button" onClick={()=>setAssigningPayment(m)} className="rounded border border-primary-300 bg-white px-2 py-0.5 font-medium text-primary-700 hover:bg-primary-50">{allocated?'Cambiar factura':'Asignar a factura'}</button>}</div>;})()}
                        {m.category === 'cheque' && (
                          <>
                            <p className="text-xs text-gray-500">{m.checkNumber || <span className="rounded bg-amber-100 px-1.5 py-0.5 font-semibold text-amber-800">Falta el número</span>}{m.checkBank ? ` · ${m.checkBank}` : ''}</p>
                            <p className="text-xs text-gray-400">Emisión: {m.checkIssueDate ? formatDMY(m.checkIssueDate) : '-'}</p>
                            <p className="text-xs text-gray-400">Vence: {m.checkDueDate ? formatDMY(m.checkDueDate) : '-'}</p>
                            {isReadOnly ? (
                              <span className={`inline-flex rounded-full border px-2 py-1 text-xs font-semibold ${CHECK_STATUS_BADGE[m.checkStatus] || CHECK_STATUS_BADGE.pendiente}`}>{CHECK_STATUS_LABELS[m.checkStatus] || m.checkStatus}</span>
                            ) : (
                              <select value={m.checkStatus} onChange={e => changeCheckStatus(m.id, e.target.value)} className={`rounded-full border px-2 py-1 text-xs font-semibold ${CHECK_STATUS_BADGE[m.checkStatus] || CHECK_STATUS_BADGE.pendiente}`}>
                                {Object.entries(CHECK_STATUS_LABELS).map(([key, label]) => <option key={key} value={key} disabled={key === 'cobrado' && m.checkDueDate > today()}>{label}</option>)}
                              </select>
                            )}
                          </>
                        )}
                        {m.receiptId && <button onClick={() => openReceiptDetail(m)} className="flex items-center gap-1 text-xs text-primary-600 hover:text-primary-700"><FileSearch className="h-3.5 w-3.5" />Ver remito</button>}
                        {m.attachmentPath && <button type="button" onClick={() => openAttachment(m.attachmentPath)} className="flex items-center gap-1 text-xs text-primary-600 hover:text-primary-700"><Paperclip className="h-3.5 w-3.5" />Ver comprobante</button>}
                        {m.category !== 'cheque' && !m.receiptId && !m.attachmentPath && '-'}
                      </div>
                    </td>
                    <td className="px-5 py-3 text-sm text-gray-500 max-w-xs truncate">{m.note}</td>
                    <td className="px-5 py-3">
                      {!isReadOnly && (
                        <div className="flex justify-end gap-2">
                          <button onClick={() => openEdit(m)} className="rounded-lg border px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">Editar</button>
                          {confirmDeleteId === m.id ? (
                            <DeleteConfirm onConfirm={() => deleteMovement(m.id)} onCancel={() => setConfirmDeleteId(null)} />
                          ) : (
                            <button onClick={() => setConfirmDeleteId(m.id)} className="rounded-lg border border-red-200 px-3 py-2 text-sm text-red-600 hover:bg-red-50">Eliminar</button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                  );
                }
                const r = row.receipt;
                const rm = row.movement;
                return (
                  <tr key={row.key}>
                    <td className="px-5 py-3 text-sm text-gray-600">{formatDMY(r.receiptDate)}</td>
                    <td className="px-5 py-3"><span className="text-red-600 font-medium">Documento</span></td>
                    <td className="px-5 py-3">
                      {rm?.attachmentPath ? (
                        <button type="button" onClick={() => openAttachment(rm.attachmentPath)} className="inline-flex items-center gap-1.5 text-gray-700 hover:text-primary-600" title="Ver comprobante">
                          <FileText className="h-3.5 w-3.5 text-gray-400" />Remito {r.receiptNumber || ''}
                        </button>
                      ) : (
                        <span className="inline-flex items-center gap-1.5"><FileText className="h-3.5 w-3.5 text-gray-400" />Remito {r.receiptNumber || ''}</span>
                      )}
                    </td>
                    <td className="px-5 py-3 font-semibold">{rm ? formatARS(rm.amount) : <span className="rounded-full bg-amber-100 px-2 py-1 text-xs font-semibold text-amber-800">Sin monto</span>}</td>
                    <td className="px-5 py-3">
                      <div className="space-y-1">
                        <p className="text-xs text-gray-500">
                          {r.itemsCount} línea{r.itemsCount === 1 ? '' : 's'}
                          {r.hasNonStockItem && <span title="Alguna línea de este remito no carga a Stock propio" className="ml-1 inline-block rounded-full bg-gray-100 px-2 py-1 text-xs font-semibold text-gray-600">No carga stock</span>}
                          {r.hasFigurativePriceItem && <span title="Alguna línea tiene precio solo de referencia, no incluido en el monto total" className="ml-1 inline-block rounded-full bg-purple-100 px-2 py-1 text-xs font-semibold text-purple-700">Precio de ref.</span>}
                          {r.amountMismatch && <span title="El monto cargado no coincide con la suma de las líneas" className="ml-1 inline-block rounded-full bg-amber-100 px-2 py-1 text-xs font-semibold text-amber-800">⚠ no coincide</span>}
                        </p>
                        <button onClick={() => openReceiptDetailById(r.id)} className="flex items-center gap-1 text-xs text-primary-600 hover:text-primary-700"><FileSearch className="h-3.5 w-3.5" />Ver detalle</button>
                      </div>
                    </td>
                    <td className="px-5 py-3 text-sm text-gray-500 max-w-xs truncate">{r.note || '-'}</td>
                    <td className="px-5 py-3">
                      {!isReadOnly && (
                        <div className="flex justify-end gap-2">
                          <button onClick={() => openEditReceipt(r.id)} className="rounded-lg border px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">Editar</button>
                          {!r.hasMovement && <button onClick={() => openCreateForReceipt(r)} className="rounded-lg bg-primary-600 px-3 py-2 text-sm text-white hover:bg-primary-700">Cargar monto</button>}
                          {confirmDeleteReceiptId === r.id ? (
                            <DeleteConfirm onConfirm={() => deleteReceipt(r.id)} onCancel={() => setConfirmDeleteReceiptId(null)} />
                          ) : (
                            <button onClick={() => setConfirmDeleteReceiptId(r.id)} title="Elimina el remito, revierte el stock que había cargado y borra el movimiento de cuenta corriente si tenía uno" className="rounded-lg border border-red-200 px-3 py-2 text-sm text-red-600 hover:bg-red-50">Eliminar</button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            {!loading && !combinedRows.length && <tr><td colSpan={7} className="px-5 py-10 text-center text-gray-500">Todavía no hay movimientos cargados.</td></tr>}
          </tbody>
        </table>
      </div>
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 sm:items-center sm:p-4">
          <div className="w-full max-w-3xl max-h-[92vh] overflow-y-auto rounded-t-xl bg-white shadow-xl sm:max-h-[90vh] sm:rounded-xl">
            <div className="sticky top-0 z-10 flex items-center justify-between border-b bg-white px-4 py-4 sm:px-6"><h2 className="text-lg font-semibold">{editingId ? 'Editar movimiento' : 'Nuevo movimiento'}</h2><button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-600">×</button></div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 px-4 py-5 sm:px-6">
              <label className="block text-sm font-medium text-gray-700">Tipo
                <select value={form.movementType} onChange={e => setForm({ ...form, movementType: e.target.value as 'documento' | 'pago' })} className="mt-1 w-full rounded-lg border px-3 py-2">
                  <option value="documento">Documento ({type === 'supplier' ? 'aumenta lo que le debo' : 'aumenta lo que me debe'})</option>
                  <option value="pago">Pago ({type === 'supplier' ? 'reduce lo que le debo' : 'reduce lo que me debe'})</option>
                </select>
              </label>
              <label className="block text-sm font-medium text-gray-700">Categoría
                <select value={form.category} onChange={e => { const category = e.target.value; setForm({ ...form, category, invoice:type==='client'&&category==='factura'?(form.invoice||{number:'',items:[]}):null, movementType: CATEGORY_DEFAULT_TYPE[category] ?? form.movementType }); }} className="mt-1 w-full rounded-lg border px-3 py-2">
                  {Object.entries(CATEGORY_LABELS).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
                </select>
              </label>
              {CATEGORY_DEFAULT_TYPE[form.category] && CATEGORY_DEFAULT_TYPE[form.category] !== form.movementType && (
                <p className="sm:col-span-2 -mt-2 text-xs text-amber-700">
                  Normalmente un {CATEGORY_LABELS[form.category].toLowerCase()} es un {CATEGORY_DEFAULT_TYPE[form.category] === 'pago' ? 'Pago' : 'Documento'}. Revisá el tipo antes de guardar.
                </p>
              )}
              {type==='client'&&form.category==='factura'&&form.movementType==='documento'&&<div className="sm:col-span-2 space-y-2"><label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!form.invoice} onChange={e=>setForm({...form,invoice:e.target.checked?{number:'',items:[]}:null})}/>Discriminar factura por artículo y pedido</label>{form.invoice?<ClientInvoiceEditor clientId={id} invoice={form.invoice} movements={movements} editingId={editingId} onChange={invoice=>setForm({...form,invoice,amount:!editingId&&invoice.items.length?invoiceTotals(invoice).total:form.amount})}/>:<p className="text-xs text-amber-800">Sin detalle, esta factura cuenta en el saldo pero no identifica productos facturados en Estadísticas.</p>}</div>}
              {type==='client'&&form.movementType==='pago'&&<div className="sm:col-span-2 space-y-2 rounded-lg border p-3"><p className="text-sm font-medium">Asignar pago a facturas (opcional)</p>{(()=>{const original=editingId?movements.find(x=>x.id===editingId)?.allocations||[]:[];const setAlloc=(invoiceId:number,amount:number)=>setForm({...form,allocations:[...form.allocations.filter(a=>a.invoiceId!==invoiceId),...(amount>0?[{invoiceId,amount}]:[])]});const assigned=form.allocations.reduce((sum,a)=>sum+a.amount,0);const rows=movements.filter(m=>m.movementType==='documento'&&m.category==='factura').map(m=>({m,available:Number(m.unassignedBalance??m.amount)+(original.find(a=>a.invoiceId===m.id)?.amount||0),current:form.allocations.find(a=>a.invoiceId===m.id)?.amount||0})).filter(r=>r.available>0.01||r.current>0);return <>{!rows.length&&<p className="text-xs text-gray-500">No hay facturas con saldo pendiente.</p>}{rows.map(({m,available,current})=>{const after=Math.round((available-current)*100)/100;return <div key={m.id} className={`flex flex-wrap items-start justify-between gap-2 text-xs ${current>0?'rounded bg-blue-50 px-1':''}`}><span className="pt-1.5">Factura {m.invoice?.number||'sin número'} · {formatDMY(m.movementDate)} · {formatARS(m.amount)}<span className="block text-gray-500">{current>0?(Math.abs(after)<0.01?'con este pago queda cubierta':after>0?`con este pago le falta ${formatPesos(after)}`:`te pasás ${formatPesos(-after)}`):`le falta ${formatPesos(available)}`}</span></span><span className="flex items-start gap-1"><span className="w-36"><MoneyInput value={current} onChange={v=>setAlloc(m.id,v)} ariaLabel={`Importe para factura ${m.id}`} className="w-full rounded border px-2 py-1"/></span><button type="button" onClick={()=>setAlloc(m.id,Math.round(Math.min(available,Math.max(0,Number(form.amount||0)-assigned+current))*100)/100)} className="rounded border px-2 py-1 text-primary-700">Asignar</button></span></div>;})}<p className={`text-xs ${assigned>Number(form.amount||0)+0.01?'text-red-700':'text-gray-600'}`}>Asignado: {formatPesos(assigned)} de {formatPesos(Number(form.amount||0))}.{assigned>Number(form.amount||0)+0.01?` Te pasaste ${formatPesos(assigned-Number(form.amount||0))}.`:Number(form.amount||0)-assigned>0.004&&assigned>0?` Quedan ${formatPesos(Number(form.amount||0)-assigned)} sin asignar.`:''} El estado del cheque se mantiene por separado.</p></>;})()}</div>}
              <label className="block text-sm font-medium text-gray-700">Monto (ARS)<input type="number" min={0} step="0.01" readOnly={!editingId&&type==='client'&&form.category==='factura'&&form.movementType==='documento'&&!!form.invoice?.items.length} value={form.amount} onChange={e => setForm({ ...form, amount: Number(e.target.value) })} className="mt-1 w-full rounded-lg border px-3 py-2" />{editingId&&type==='client'&&form.category==='factura'&&form.movementType==='documento'&&!!form.invoice?.items.length&&Math.abs(invoiceTotals(form.invoice).total-form.amount)>0.02&&<button type="button" onClick={()=>{ setForm({ ...form, amount: invoiceTotals(form.invoice!).total }); setFormError(''); }} className="mt-1 text-xs font-normal text-blue-800 hover:underline">Recalcular monto según el detalle ({formatARS(invoiceTotals(form.invoice).total)})</button>}</label>
              <label className="block text-sm font-medium text-gray-700">Fecha<input type="date" value={form.movementDate} onChange={e => setForm({ ...form, movementDate: e.target.value })} className="mt-1 w-full rounded-lg border px-3 py-2" /></label>

              {form.category === 'cheque' && (
                <>
                  <label className="block text-sm font-medium text-gray-700">N° de cheque <span className="font-normal text-gray-400">(opcional)</span><input value={form.checkNumber} onChange={e => setForm({ ...form, checkNumber: e.target.value })} className="mt-1 w-full rounded-lg border px-3 py-2" /></label>
                  <label className="block text-sm font-medium text-gray-700">Banco<input value={form.checkBank} onChange={e => setForm({ ...form, checkBank: e.target.value })} className="mt-1 w-full rounded-lg border px-3 py-2" /></label>
                  <label className="block text-sm font-medium text-gray-700">Fecha de emisión<input type="date" value={form.checkIssueDate} onChange={e => setForm({ ...form, checkIssueDate: e.target.value })} className="mt-1 w-full rounded-lg border px-3 py-2" /></label>
                  <label className="block text-sm font-medium text-gray-700">Fecha de cobro<input type="date" value={form.checkDueDate} onChange={e => setForm({ ...form, checkDueDate: e.target.value })} className="mt-1 w-full rounded-lg border px-3 py-2" /></label>
                  <label className="block text-sm font-medium text-gray-700 col-span-2">Estado
                    <select value={form.checkStatus} onChange={e => setForm({ ...form, checkStatus: e.target.value })} className="mt-1 w-full rounded-lg border px-3 py-2">
                      {Object.entries(CHECK_STATUS_LABELS).map(([key, label]) => <option key={key} value={key} disabled={key === 'cobrado' && form.checkDueDate > today()}>{label}</option>)}
                    </select>
                    <span className="mt-1 block text-xs font-normal text-gray-500">Marcá acreditado/debitado solamente cuando el movimiento ya impactó en el banco.</span>
                  </label>
                </>
              )}

              <label className="block text-sm font-medium text-gray-700 col-span-2">Nota<input value={form.note} onChange={e => setForm({ ...form, note: e.target.value })} className="mt-1 w-full rounded-lg border px-3 py-2" /></label>
              <div className="col-span-2">
                <AttachmentDropzone value={form.attachmentPath} uploading={uploading} onFileSelected={handleAttachmentChange} enabled={showForm} />
              </div>
            </div>
            {formError && <div className="mx-6 mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-red-700">{formError}</div>}
            <div className="sticky bottom-0 flex justify-end gap-2 border-t bg-white px-4 py-4 sm:px-6 [&>button]:flex-1 sm:[&>button]:flex-none"><button onClick={() => setShowForm(false)} className="rounded-lg border px-4 py-2">Cancelar</button><button onClick={submitForm} disabled={saving || uploading} className="rounded-lg bg-primary-600 px-4 py-2 text-white disabled:opacity-50">{saving ? 'Guardando...' : 'Guardar'}</button></div>
          </div>
        </div>
      )}

      {(showReceiptForm || editingReceipt) && (
        <SupplierReceiptModal
          supplierId={id}
          editingReceipt={editingReceipt}
          onClose={() => { setShowReceiptForm(false); setEditingReceipt(null); }}
          onSaved={load}
        />
      )}

      {(receiptDetail || receiptDetailError) && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 sm:items-center sm:p-4">
          <div className="w-full max-w-2xl max-h-[92vh] overflow-y-auto rounded-t-xl bg-white shadow-xl sm:max-h-[90vh] sm:rounded-xl">
            <div className="sticky top-0 z-10 flex items-center justify-between border-b bg-white px-4 py-4 sm:px-6">
              <h2 className="text-lg font-semibold">Detalle del remito {receiptDetail?.receiptNumber ? `#${receiptDetail.receiptNumber}` : ''}</h2>
              <button onClick={() => { setReceiptDetail(null); setReceiptDetailError(''); }} className="text-gray-400 hover:text-gray-600">×</button>
            </div>
            <div className="px-4 py-5 sm:px-6">
              {receiptDetailError && <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-red-700">{receiptDetailError}</div>}
              {receiptDetail && (
                <>
                  <div className="mb-3 flex justify-end"><WeightToggle /></div>
                  <p className="mb-3 text-sm text-gray-500">{formatDMY(receiptDetail.receiptDate)} · {formatARS(receiptDetail.amount)}{receiptDetail.note ? ` · ${receiptDetail.note}` : ''}</p>
                  {receiptDetail.attachmentPath && (
                    <div className="mb-4">
                      {/\.pdf$/i.test(receiptDetail.attachmentPath) ? (
                        <a href={toProductImageUrl(receiptDetail.attachmentPath)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700"><Paperclip className="h-3.5 w-3.5" />Ver comprobante (PDF)</a>
                      ) : (
                        <button type="button" onClick={() => setZoomImage(toProductImageUrl(receiptDetail.attachmentPath))} className="cursor-zoom-in">
                          <img src={toProductImageUrl(receiptDetail.attachmentPath)} alt="Comprobante del remito" className="max-h-64 rounded-lg border border-gray-200 object-contain hover:opacity-90" />
                        </button>
                      )}
                    </div>
                  )}
                  <div className="overflow-x-auto rounded-lg border border-gray-200">
                    <table className="min-w-full text-xs">
                      <thead className="bg-gray-50 text-gray-500">
                        <tr>
                          <th className="px-3 py-1.5 text-left">Medida</th>
                          <th className="px-3 py-1.5 text-left">Color</th>
                          <th className="px-3 py-1.5 text-right">Packs</th>
                          <th className="px-3 py-1.5 text-right">Bolsas</th>
                          {showWeight && <th className="px-3 py-1.5 text-right" title="Ancho × largo × micrones × 2 × 0,92 ÷ 10.000. Debajo, el pack completo.">Peso est. bolsa / pack</th>}
                          {receiptDetail.items.some(item => item.unitPrice) && <>
                            <th className="px-3 py-1.5 text-right">Precio unit.</th>
                            <th className="px-3 py-1.5 text-right" title="Precio unitario del remito × 50 bolsas">Precio pack</th>
                            <th className="px-3 py-1.5 text-right">Importe</th>
                          </>}
                          {receiptDetail.items.some(item => item.itemNote || item.priceFigurativeNote) && <th className="px-3 py-1.5 text-left">Nota</th>}
                          {receiptDetail.items.some(item => item.loadStock === false) && <th className="px-3 py-1.5 text-left">Stock propio</th>}
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {receiptDetail.items.map((item, index) => (
                          <tr key={index} className={item.loadStock === false ? 'bg-amber-50' : undefined}>
                            <td className="px-3 py-1.5 font-medium">{item.ancho} x {item.largo} cm{item.microns ? <span className="font-normal text-gray-400"> · {item.microns}μ</span> : null}</td>
                            <td className="px-3 py-1.5"><span className="inline-flex min-w-16 justify-center rounded px-2 py-0.5 font-semibold" style={receiptColorStyle(item.color)}>{COLOR_LABELS[item.color as ColorKey] || item.color}</span></td>
                            <td className="px-3 py-1.5 text-right">{Math.floor(item.totalBolsas / PACK_SIZE)}{item.totalBolsas % PACK_SIZE ? ` + ${item.totalBolsas % PACK_SIZE}` : ''}</td>
                            <td className="px-3 py-1.5 text-right">{item.totalBolsas.toLocaleString('es-AR')}</td>
                            {showWeight && <td className="whitespace-nowrap px-3 py-1.5 text-right"><BagWeight grams={bagWeightGrams(item.ancho, item.largo, Number(item.microns || 0))} /></td>}
                            {receiptDetail.items.some(i => i.unitPrice) && <>
                              <td className="px-3 py-1.5 text-right">{item.unitPrice ? formatARS(item.unitPrice) : '-'}</td>
                              <td className="whitespace-nowrap px-3 py-1.5 text-right">{item.unitPrice ? formatARS(item.unitPrice * PACK_SIZE) : '-'}</td>
                              <td className="px-3 py-1.5 text-right">
                                {item.priceIsFigurative
                                  ? <span title={`Precio de referencia, no suma al total${item.priceFigurativeNote ? `: ${item.priceFigurativeNote}` : ''}`} className="cursor-help text-gray-400 underline decoration-dotted">ref.</span>
                                  : item.subtotal ? formatARS(item.subtotal) : '-'}
                              </td>
                            </>}
                            {receiptDetail.items.some(i => i.itemNote || i.priceFigurativeNote) && (
                              <td className="px-3 py-1.5 text-gray-600">
                                {item.itemNote}
                                {item.itemNote && item.priceFigurativeNote ? ' · ' : ''}
                                {item.priceFigurativeNote && <span>Precio de referencia: {item.priceFigurativeNote}</span>}
                                {!item.itemNote && !item.priceFigurativeNote && '-'}
                              </td>
                            )}
                            {receiptDetail.items.some(i => i.loadStock === false) && <td className="px-3 py-1.5">{item.loadStock === false ? <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-700">No carga</span> : '-'}</td>}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {assigningPayment && <AssignPaymentModal payment={assigningPayment} movements={movements} onClose={() => setAssigningPayment(null)} onSaved={load} />}
      {collectionFor && <CollectionModal clientId={id} invoices={pendingInvoices} preselected={collectionFor} onClose={() => setCollectionFor(null)} onSaved={load} />}

      {zoomImage && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 p-4 cursor-zoom-out" onClick={() => setZoomImage(null)}>
          <button onClick={() => setZoomImage(null)} className="absolute top-4 right-4 text-white text-3xl leading-none hover:text-gray-300">×</button>
          <img src={zoomImage} alt="Comprobante ampliado" className="max-h-full max-w-full object-contain" onClick={e => e.stopPropagation()} />
        </div>
      )}
    </div>
  );
};

export default CuentaCorrienteDetalle;
